import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { TextField } from "@mui/material";

import { TextContent } from "js/Components/TextContent";
import { Dropdown } from "js/Components/Dropdown";
import { MenuItem } from "js/Components/Menu";

import { guessUserCountry, getPostalCodeProps, getAllCountryCodes } from "../../utils/countries";
import type { AddressFormProps } from "./AddressForm";

const AddressContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-end;
    width: 100%;
`;

const InputContainer = styled.div<{ isSingle?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    width: ${({ isSingle }) => isSingle ? "100%" : "calc(50% - 6px)"};

    >label {
        font-family: "Source Sans Pro", sans-serif;
        font-size: 14.88px;
        font-weight: 400;
        line-height: 17.112px;
        color: rgb(48, 49, 61);
        margin-bottom: 4px;
    }
`;

const StyledDropdown = styled(Dropdown)`
    &&& {
        width: 100%;

        .MuiSelect-select {
            padding: 6px 32px 7px 0px;
            font-size: 16px;
            font-weight: 400;
            color: #222;
        }

        .MuiOutlinedInput-notchedOutline {
            border: none;
            border-bottom: 1px solid #ccc;
        }

        .MuiSelect-root {
            background-color: transparent;
        }

        &.Mui-focused {
            .MuiOutlinedInput-notchedOutline {
                border-color: #11a9e2;
            }
        }
    }
`;

const StyledTextField = styled(TextField) <{ warning: boolean }>`
    &&& {
        >:not(.Mui-focused)>.MuiOutlinedInput-notchedOutline {
            border-color: ${({ warning }) => warning ? "red" : "rgba(0, 0, 0, 0.23)"};
            box-shadow: ${({ warning }) => warning ? "0 0 0px 1px #ff0000;" : "none"};
        }

        .Mui-focused>.MuiOutlinedInput-notchedOutline {
            border-width: 1px;
            border-color: #11a9e2;
            box-shadow: 0 0 0px 1px #11a9e2;
        }

        :hover>:not(.Mui-focused)>.MuiOutlinedInput-notchedOutline {
            border-color: #222;
        }

        input::placeholder {
            font-style: normal;
        }

        label.MuiInputLabel-shrink {
            color: #666;
            font-size: 19.2px;
            line-height: 20px;
            transform: translate(0, -6.5px) scale(0.75)
        }
    }
`;

const ErrorMessage = styled.div`
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    font-family: "Source Sans Pro", sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 16.112px;
    color: red;
`;

export function AddressForm({ initialAddress, onChange, disabled }: AddressFormProps) {
    const { t } = useTranslation();
    const [countryCode, setCountryCode] = useState(initialAddress.country || guessUserCountry());
    const [postalCode, setPostalCode] = useState(initialAddress.postal_code);
    const [showError, setShowError] = useState(false);

    const countryCodes = getAllCountryCodes();
    const postalCodeProps = getPostalCodeProps(countryCode);
    const isAddressValid = postalCodeProps.validate(postalCode);

    useEffect(() => {
        onChange({
            address: {
                country: countryCode,
                postal_code: postalCodeProps.show ? postalCode : ""
            },
            isAddressValid,
        });
    }, [countryCode, postalCode]);

    const handleCountryCodeChange = (countryCode: string) => {
        setCountryCode(countryCode);
        setPostalCode("");
    };

    const handlePostalCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPostalCode(postalCodeProps.normalize(event.target.value));
    };

    const handleBlur = () => setShowError(true);
    const handleFocus = () => setShowError(false);

    return (
        <AddressContainer>
            <InputContainer isSingle={!postalCodeProps.show}>
                <TextContent label>{t("Country")}</TextContent>
                <StyledDropdown
                    value={countryCode}
                    onChange={handleCountryCodeChange}
                    onBlur={handleBlur}
                    disabled={disabled}
                    data-testid="minimal-billing-address-form-country"
                >
                    {countryCodes.map(({ code, name }) => (
                        <MenuItem key={code} value={code.toUpperCase()}>{name}</MenuItem>
                    ))}
                </StyledDropdown>
            </InputContainer>
            {postalCodeProps.show && <InputContainer isSingle={false}>
                <StyledTextField
                    placeholder={postalCodeProps.placeholder}
                    onChange={handlePostalCodeChange}
                    value={postalCode}
                    fullWidth
                    disabled={disabled}
                    autoFocus={false}
                    autoComplete="off"
                    warning={!isAddressValid && showError}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    data-testid="minimal-billing-address-form-postal-code"
                    label={postalCodeProps.name}
                />
                {!isAddressValid && showError && <ErrorMessage>{t("Invalid {{field}}", { field: postalCodeProps.name })}</ErrorMessage>}
            </InputContainer>}
        </AddressContainer>
    );
}
